import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { useCategories, useMasterData } from '../masterdata/queries';
import type { MacroAction, MacroActionType } from '../shortcuts/api';
import { flattenCategories, STATE_CATEGORIES } from '../tickets/presentation';
import { Select } from '../../components/Select';

/**
 * Aktionen zusammenstellen -- dieselbe Liste wie beim Makro (F3-11).
 *
 * Der Baustein liegt hier und nicht bei den Makros, weil ihn drei Stellen
 * brauchen: Makro, Regel und Zeitplan. Die Aktionsliste kommt aus dem
 * erzeugten Schema; kommt serverseitig eine Aktion dazu, erscheint sie hier
 * von selbst -- allerdings ohne Uebersetzung, und genau daran faellt sie auf.
 */

/** Aktionen, die einen Stammdatensatz waehlen. */
const FIELD_ACTIONS: readonly MacroActionType[] = [
  'set_queue',
  'set_type',
  'set_priority',
  'set_category',
];

/** Aktionen, die einen Text tragen. */
const TEXT_ACTIONS: readonly MacroActionType[] = ['add_note', 'add_reply'];

/** Aktionen, die ihren Wert selbst ermitteln -- die automatische Zuweisung. */
const VALUELESS_ACTIONS: readonly MacroActionType[] = ['assign_round_robin', 'assign_least_loaded'];

const OFFERED: readonly MacroActionType[] = [
  ...FIELD_ACTIONS,
  'set_state',
  'add_tag',
  'remove_tag',
  ...VALUELESS_ACTIONS,
  ...TEXT_ACTIONS,
];

interface ActionEditorProps {
  value: MacroAction[];
  onChange: (next: MacroAction[]) => void;
}

export function ActionEditor({ value, onChange }: ActionEditorProps) {
  const { t } = useTranslation();

  const queues = useMasterData('queue');
  const types = useMasterData('type');
  const priorities = useMasterData('priority');
  const categories = useCategories();
  const tags = useMasterData('tag');

  const [draftType, setDraftType] = useState<MacroActionType>('set_priority');
  const [draftValue, setDraftValue] = useState('');

  const optionsFor = (type: MacroActionType): { id: string; label: string }[] => {
    switch (type) {
      case 'set_queue':
        return (queues.data ?? []).map((entry) => ({ id: entry.id, label: entry.name }));
      case 'set_type':
        return (types.data ?? []).map((entry) => ({ id: entry.id, label: entry.name }));
      case 'set_priority':
        return (priorities.data ?? []).map((entry) => ({ id: entry.id, label: entry.name }));
      case 'set_category':
        return flattenCategories(categories.data ?? []);
      case 'add_tag':
      case 'remove_tag':
        return (tags.data ?? []).map((entry) => ({ id: entry.id, label: entry.name }));
      case 'set_state':
        return STATE_CATEGORIES.map((category) => ({
          id: category,
          label: t(`stateCategory.${category}`),
        }));
      default:
        return [];
    }
  };

  const needsText = TEXT_ACTIONS.includes(draftType);
  const needsNothing = VALUELESS_ACTIONS.includes(draftType);
  const options = optionsFor(draftType);

  const label = (action: MacroAction): string => {
    if (VALUELESS_ACTIONS.includes(action.type)) return '';
    if (TEXT_ACTIONS.includes(action.type)) return action.value ?? '';
    const match = optionsFor(action.type).find((option) => option.id === action.value);
    return match?.label ?? action.value ?? '';
  };

  return (
    <div className="stack">
      {value.length === 0 ? (
        <p className="table__muted">{t('macros.noActions')}</p>
      ) : (
        <ol className="task-list">
          {value.map((action, index) => (
            <li key={`${action.type}-${String(index)}`} className="task-list__item">
              <Badge tone="neutral">{t(`macroAction.${action.type}`)}</Badge>
              <span>{label(action)}</span>
              <Button
                variant="ghost"
                onClick={() => {
                  onChange(value.filter((_, position) => position !== index));
                }}
              >
                {t('masterdata.delete')}
              </Button>
            </li>
          ))}
        </ol>
      )}

      <div className="row">
        <Select
          className="field__control"
          aria-label={t('macros.actionType')}
          value={draftType}
          onChange={(event) => {
            setDraftType(event.target.value as MacroActionType);
            setDraftValue('');
          }}
        >
          {OFFERED.map((type) => (
            <option key={type} value={type}>
              {t(`macroAction.${type}`)}
            </option>
          ))}
        </Select>

        {needsNothing ? null : needsText ? (
          <input
            className="field__control"
            aria-label={t('macros.actionText')}
            placeholder={t('macros.actionText')}
            value={draftValue}
            onChange={(event) => {
              setDraftValue(event.target.value);
            }}
          />
        ) : (
          <Select
            className="field__control"
            aria-label={t('macros.actionValue')}
            value={draftValue}
            onChange={(event) => {
              setDraftValue(event.target.value);
            }}
          >
            <option value="">{t('macros.chooseValue')}</option>
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </Select>
        )}

        <Button
          variant="secondary"
          disabled={!needsNothing && draftValue.trim() === ''}
          onClick={() => {
            onChange([
              ...value,
              needsNothing ? { type: draftType } : { type: draftType, value: draftValue },
            ]);
            setDraftValue('');
          }}
        >
          {t('macros.addAction')}
        </Button>
      </div>
    </div>
  );
}
